// src/pages/AssessmentInstructions.jsx
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  IconButton
} from '@mui/material';

import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Info as InfoIcon
} from '@mui/icons-material';

export default function AssessmentInstructions() {
  const navigate = useNavigate();
  const location = useLocation();
  const sessionToken = sessionStorage.getItem('sessionToken');

  const steps = [
    'Organization Details',
    'Instructions',
    'Assessment Questions',
    'Review & Submit',
    'Results'
  ];

  const answerOptions = [
    {
      code: 'incomplete',
      title: 'Incomplete',
      description: 'No evidence of the practice',
      color: '#f44336'
    },
    {
      code: 'documented',
      title: 'Documented',
      description: 'Policy exists but implementation is partial or not verified',
      color: '#ff9800'
    },
    {
      code: 'operational',
      title: 'Operational',
      description:
        'Practice is implemented and functioning with evidence of active use',
      color: '#2196f3'
    },
    {
      code: 'verified',
      title: 'Verified',
      description:
        'Practice is verified by third party or demonstrated through external evidence',
      color: '#4caf50'
    }
  ];

  const handleContinue = () => {
    if (sessionToken) {
      navigate('/assessment/questions', {
        state: { sessionToken }
      });
    } else {
      navigate('/');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box className="raise-blue" sx={{ bgcolor: 'primary.main', color: 'white', py: 2 }}>
        <Container>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}
          >
            <IconButton
              onClick={() => navigate('/')}
              sx={{ color: 'white', position: 'absolute', left: 0 }}
            >
              <ArrowBackIcon />
            </IconButton>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <InfoIcon sx={{ mr: 2, fontSize: 28 }} />
              <Typography variant="h5" sx={{ fontWeight: 600,color: 'white' }}>
                Assessment Instructions
              </Typography>
            </Box>
          </Box>

          {/* Stepper */}
          <Box sx={{ mt: 3 }}>
            <Stepper activeStep={1} alternativeLabel>
              {steps.map((label, idx) => (
                <Step key={label}>
                  <StepLabel
                    sx={{
                      '& .MuiStepLabel-label': { color: 'white !important' },
                      '& .MuiStepIcon-root': {
                        color: idx <= 1 ? '#fff !important' : undefined
                      },
                      '& .MuiStepIcon-text': {
                        fill: idx <= 1 ? '#004494' : undefined
                      }
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Container>
      </Box>

      {/* Main */}
      <Container sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, maxWidth: 1000, mx: 'auto' }}>
          <Typography
            variant="h4"
            sx={{ mb: 3, fontWeight: 600, textAlign: 'center' }}
          >
            How to Answer Assessment Questions
          </Typography>

          <Typography
            variant="body1"
            sx={{ mb: 4, color: 'text.secondary', textAlign: 'center' }}
          >
            For each question, select the option that best describes your
            organization&apos;s current state. Be honest and accurate in your responses
            to get the most valuable insights.
          </Typography>

          <Typography
            variant="h5"
            sx={{ mb: 3, fontWeight: 600, textAlign: 'center' }}
          >
            Answer Options Explained
          </Typography>

          
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 3,
              mb: 4
            }}
          >
            {answerOptions.map((option, index) => (
              <Card
                key={option.code}
                sx={{
                  height: '100%',
                  border: 2,
                  borderColor: option.color,
                  '&:hover': { boxShadow: 4 }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2
                    }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: option.color,
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                        fontSize: '0.875rem',
                        fontWeight: 600
                      }}
                    >
                      {index + 1}
                    </Box>

                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: option.color }}
                    >
                      {option.title}
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    sx={{
                      mb: 2,
                      fontWeight: 500,
                      textAlign: "justify"
                    }}
                  >
                    {option.description}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', fontStyle: 'italic' }}
                  ></Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Guidelines */}
          <Paper
            sx={{
              p: 3,
              bgcolor: 'background.accent',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Typography
              variant="h6"
              sx={{ mb: 2, fontWeight: 600, textAlign: 'center' }}
            >
              Assessment Guidelines
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 2
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • Answer based on your current state, not future plans
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • Provide evidence where possible to support your answers
                </Typography>
                <Typography variant="body2">
                  • Your progress is automatically saved
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • You can go back and modify answers before final submission
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • Each question focuses on a specific RAISE principle
                </Typography>
                <Typography variant="body2">
                  • Upload supporting documents when available
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Footer Buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 4
            }}
          >
            <Button
  onClick={() => navigate('/')}
  startIcon={<ArrowBackIcon />}
  sx={{ 
    color: '#004494',
    '&:hover': {
      backgroundColor: 'rgba(0, 68, 148, 0.04)'
    }
  }}
>
  Back to Home
</Button>

            <Button
              onClick={handleContinue}
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{ minWidth: 200 }}
              className='raise-blue'
            >
              Start Questions
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}